import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionBootstrap } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { SectionHead, ErrorBanner, Badge, CardHead } from '../../components/ui/Primitives.js';
import { QrCode } from '../provisioning/MobileProvisioning.js';
import { relativeTime } from '../../lib/format.js';

/**
 * The signed-in person's own security: two-step verification (docs/06 §2.1; owner, 18 September
 * 2026: optional for everyone). An authenticator app's code at sign-in, and in place of the
 * password whenever something sensitive asks them to confirm it is them.
 */

interface MfaStatus { enabled: boolean; enabled_at: string | null; recovery_codes_left: number }
const KEY = ['auth', 'mfa'] as const;

export function AccountSecurityPage() {
  const session = useSession((s) => s.session);
  const status = useQuery({ queryKey: KEY, queryFn: () => api.get<MfaStatus>('/v1/auth/mfa') });

  return (
    <>
      <SectionHead title="Your account" sub={session?.user.email ?? ''} />
      <div className="arc-card" style={{ maxWidth: 720 }}>
        <CardHead title="Two-step verification" action={status.data && (
          <Badge tone={status.data.enabled ? 'ok' : 'muted'}>
            <span className="arc-dot" aria-hidden="true" />{status.data.enabled ? 'on' : 'off'}
          </Badge>
        )} />
        <div className="arc-card-body">
          {status.error && <ErrorBanner error={status.error} />}
          {status.data && (status.data.enabled ? <MfaOn status={status.data} /> : <MfaSetup />)}
        </div>
      </div>
    </>
  );
}

/** Reloads the session so every prompt knows 2FA changed. */
function useRefreshSession() {
  const setSession = useSession((s) => s.setSession);
  const qc = useQueryClient();
  return async () => {
    void qc.invalidateQueries({ queryKey: KEY });
    setSession(await api.get<SessionBootstrap>('/v1/auth/me'));
  };
}

function MfaSetup() {
  const refresh = useRefreshSession();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const start = useMutation({ mutationFn: () => api.post<{ secret: string; otpauth_uri: string }>('/v1/auth/mfa/setup', {}) });
  const confirm = useMutation({
    mutationFn: () => api.post<{ recovery_codes: string[] }>('/v1/auth/mfa/confirm', { code }),
    onSuccess: (r) => setCodes(r.recovery_codes),
  });

  if (codes) return <RecoveryCodes codes={codes} onDone={() => void refresh()} />;

  if (!start.data) {
    return (
      <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <p className="arc-muted" style={{ margin: 0, fontSize: 12.5, flex: '1 1 300px' }}>
          Ask for a code from an authenticator app (Google Authenticator, Microsoft Authenticator,
          1Password) when you sign in, as well as your password.
        </p>
        {start.error && <ErrorBanner error={start.error} />}
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={start.isPending}
          onClick={() => start.mutate()}>
          {start.isPending ? 'Starting…' : 'Set up'}
        </button>
      </div>
    );
  }

  const submit = (e: FormEvent) => { e.preventDefault(); confirm.mutate(); };
  return (
    <div className="arc-row" style={{ gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <QrCode text={start.data.otpauth_uri} />
      <form className="arc-stack" style={{ gap: 10, flex: '1 1 260px' }} onSubmit={submit}>
        <span style={{ fontWeight: 550 }}>1. Scan this in your authenticator app</span>
        <span className="arc-subtle" style={{ fontSize: 12 }}>
          Or enter this key: <span className="arc-mono" style={{ wordBreak: 'break-all' }}>{start.data.secret}</span>
        </span>
        <span style={{ fontWeight: 550, marginTop: 6 }}>2. Enter the 6-digit code it shows</span>
        {confirm.error && <ErrorBanner error={confirm.error} />}
        <div className="arc-row" style={{ gap: 8 }}>
          <input className="arc-input arc-mono" style={{ maxWidth: 160 }} inputMode="numeric"
            autoComplete="one-time-code" maxLength={6} value={code} aria-label="Code from the app"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm"
            disabled={code.length !== 6 || confirm.isPending}>
            {confirm.isPending ? 'Checking…' : 'Turn on'}
          </button>
        </div>
      </form>
    </div>
  );
}

function MfaOn({ status }: { status: MfaStatus }) {
  const refresh = useRefreshSession();
  const [action, setAction] = useState<'off' | 'codes' | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const run = useMutation({
    mutationFn: async () => {
      if (action === 'off') {
        await api.post('/v1/auth/mfa/disable', { code });
        await refresh();
        return null;
      }
      return (await api.post<{ recovery_codes: string[] }>('/v1/auth/mfa/recovery-codes', { code })).recovery_codes;
    },
    onSuccess: (r) => { setCode(''); setAction(null); if (r) setCodes(r); },
  });

  if (codes) return <RecoveryCodes codes={codes} onDone={() => { setCodes(null); void refresh(); }} />;

  return (
    <div className="arc-stack" style={{ gap: 12 }}>
      <span className="arc-muted" style={{ fontSize: 12.5 }}>
        On{status.enabled_at ? ` since ${relativeTime(status.enabled_at)}` : ''}. Your sign-in asks for a code from your app.
        {' '}{status.recovery_codes_left} of 10 recovery codes left.
      </span>
      {run.error && <ErrorBanner error={run.error} />}
      {action ? (
        <form className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }} onSubmit={(e) => { e.preventDefault(); run.mutate(); }}>
          <input className="arc-input arc-mono" style={{ maxWidth: 200 }} autoFocus autoComplete="one-time-code"
            placeholder="Code from your app" value={code} maxLength={20} aria-label="Code from your app"
            onChange={(e) => setCode(e.target.value)} />
          <button type="submit" className={action === 'off' ? 'arc-btn arc-btn-danger arc-btn-sm' : 'arc-btn arc-btn-primary arc-btn-sm'}
            disabled={code.trim().length < 6 || run.isPending}>
            {action === 'off' ? 'Turn off' : 'Make new codes'}
          </button>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => { setAction(null); setCode(''); }}>Cancel</button>
        </form>
      ) : (
        <div className="arc-row" style={{ gap: 8 }}>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setAction('codes')}>New recovery codes</button>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setAction('off')}>Turn off</button>
        </div>
      )}
    </div>
  );
}

/** Shown once: each works one time if the phone is lost. */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="arc-stack" style={{ gap: 12 }}>
      <span style={{ fontWeight: 550 }}>Save your recovery codes</span>
      <span className="arc-muted" style={{ fontSize: 12.5 }}>
        Each works once, in place of a code, if you lose your phone. They are not shown again.
      </span>
      <div className="arc-mono" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: '6px 28px', fontSize: 13 }}>
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="arc-row" style={{ gap: 8 }}>
        <button type="button" className="arc-btn arc-btn-sm"
          onClick={() => { void navigator.clipboard?.writeText(codes.join('\n')); }}>Copy</button>
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={onDone}>I have saved them</button>
      </div>
    </div>
  );
}
